import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IApplicationRentalUnits } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field on a specific rental unit (by GUID) in the React Query cache.
 * Searches all properties to find the unit — no need to pass propertyGuid to deep leaf components.
 *
 * Replaces the `const [x, set_x] = useRecoilState(someAtomFamily(unitGuid))` pattern.
 *
 * @param unitGuid  - GUID of the rental unit to read/write
 * @param selector  - reads the field value from IApplicationRentalUnits
 * @param updater   - returns a new IApplicationRentalUnits with the field updated
 * @returns         [currentValue, setter]
 *
 * @example
 * const [rent, set_rent] = useRentalUnitField(
 *     unitGuid,
 *     (u) => u.rent,
 *     (u, v) => ({ ...u, rent: v })
 * );
 */
export function useRentalUnitField<T>(
    unitGuid: string,
    selector: (unit: IApplicationRentalUnits) => T,
    updater: (unit: IApplicationRentalUnits, value: T) => IApplicationRentalUnits
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            for (const property of d.information?.properties?.items ?? []) {
                const unit = property?.rentalUnits?.find((u) => u.guid === unitGuid);
                if (unit !== undefined) return selector(unit);
            }
            return undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const items = old.information?.properties?.items ?? [];
            const propIdx = items.findIndex((p) => p?.rentalUnits?.some((u) => u.guid === unitGuid));
            if (propIdx === -1) return old;
            const property = items[propIdx];
            const units = property?.rentalUnits ?? [];
            const unitIdx = units.findIndex((u) => u.guid === unitGuid);
            if (unitIdx === -1) return old;
            const newUnits = [...units];
            newUnits[unitIdx] = updater(newUnits[unitIdx], value);
            const newItems = [...items];
            newItems[propIdx] = { ...property, rentalUnits: newUnits };
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        items: newItems,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
